import type { SocialPlatform } from "@brandex/api-client";
import { formatRelative, type MessageKey } from "@brandex/i18n";
import { Alert, Avatar, Badge, Button, PlatformIcon, Spinner, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, X } from "lucide-react";
import { useCallback, useState, useSyncExternalStore } from "react";

import { useBrandex, useT } from "../../context";
import { errorMessage } from "../../errors";
import { useCurrentOrg } from "../../client-scope";
import { keys, useSocial } from "../../queries";
import { SOCIAL_KEYS, isSocialKey, type SocialKey } from "./guides";
import { PlatformPanel, STATUS_TONE, compact, onSite } from "./panel";

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

const currentSearch = () => window.location.search;
const serverSearch = () => "";

/**
 * The open platform tab, kept in the address (`?platform=youtube`) so links
 * from notifications, refresh and the back button work. A query string, not
 * a route segment: the desktop app is a static export.
 */
export function usePlatformTab() {
  const search = useSyncExternalStore(subscribe, currentSearch, serverSearch);
  const raw = new URLSearchParams(search).get("platform");
  const tab: SocialKey | null = isSocialKey(raw) ? raw : null;
  const go = useCallback((next: SocialKey | null) => {
    const url = new URL(window.location.href);
    if (next === null) url.searchParams.delete("platform");
    else url.searchParams.set("platform", next);
    window.history.pushState(null, "", url);
    listeners.forEach((l) => l());
  }, []);
  return { tab, go };
}

/** What a finished sign-in left in the address: accounts to pick or a reason. */
function useSignInReturn() {
  const search = useSyncExternalStore(subscribe, currentSearch, serverSearch);
  const q = new URLSearchParams(search);
  const pick = q.get("social_pick");
  const error = q.get("social_error");
  const clear = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("social_pick");
    url.searchParams.delete("social_error");
    window.history.replaceState(null, "", url);
    listeners.forEach((l) => l());
  }, []);
  return {
    pick: pick !== null && /^[A-Za-z0-9_-]{16,200}$/.test(pick) ? pick : null,
    error: error !== null && ["expired", "denied", "failed", "no_accounts"].includes(error) ? error : null,
    clear,
  };
}

/** Picks the account a sign-in found (Page, Instagram account, location…). */
function ChooseAccount({ orgId, id, onDone }: { orgId: string; id: string; onDone: () => void }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const choice = useQuery({
    queryKey: ["orgs", orgId, "social", "choice", id],
    retry: false,
    queryFn: async () => {
      const res = await api.http.GET("/v1/orgs/{org_id}/social/oauth/choices/{choice_id}", { params: { path: { org_id: orgId, choice_id: id } } });
      if (res.data === undefined) throw new Error("Empty response body");
      return res.data;
    },
  });
  const choose = useMutation({
    mutationFn: async (accountId: string) => {
      await api.http.POST("/v1/orgs/{org_id}/social/oauth/choices/{choice_id}", {
        params: { path: { org_id: orgId, choice_id: id } },
        body: { account_id: accountId },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.social(orgId) });
      onDone();
    },
  });
  const accounts = choice.data?.accounts ?? [];
  const selected = picked ?? (accounts.length === 1 ? accounts[0]?.account_id ?? null : null);
  const name = choice.data ? t(`social.platform.${choice.data.platform}` as MessageKey) : "";
  return (
    <section aria-labelledby="choose-account" className="grid gap-4 rounded-[var(--radius-lg)] border border-primary/50 bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="choose-account" className="font-display text-lg font-semibold">{t("social.choose.title", { platform: name })}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("social.choose.lead")}</p>
        </div>
        <Button variant="ghost" size="icon" aria-label={t("common.cancel")} onClick={onDone}>
          <X aria-hidden className="h-4 w-4" />
        </Button>
      </div>
      {choice.isPending ? <Spinner label={t("common.loading")} /> : null}
      {choice.isError ? <Alert tone="error">{t("social.choose.expired")}</Alert> : null}
      {accounts.length > 0 ? (
        <ul role="radiogroup" aria-labelledby="choose-account" className="grid gap-2 sm:grid-cols-2">
          {accounts.map((a) => (
            <li key={a.account_id}>
              <label className={cn("flex cursor-pointer items-center gap-3 rounded-md border p-3", selected === a.account_id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50")}>
                <input type="radio" name="account" className="sr-only" checked={selected === a.account_id} onChange={() => setPicked(a.account_id)} />
                {a.avatar_url ? <img src={a.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" referrerPolicy="no-referrer" /> : <Avatar name={a.name} />}
                <span className="min-w-0">
                  <span className="block truncate font-medium">{a.name || a.account_id}</span>
                  {a.handle ? <span className="block truncate text-sm text-muted-foreground">{a.handle}</span> : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
      {choose.isError ? <Alert tone="error">{errorMessage(choose.error, t)}</Alert> : null}
      {accounts.length > 0 ? (
        <Button className="w-full sm:w-fit" disabled={selected === null} loading={choose.isPending} onClick={() => selected !== null && choose.mutate(selected)}>
          {t("social.choose.connect")}
        </Button>
      ) : null}
    </section>
  );
}

/**
 * Social media (ADR 0024): connect the practice's accounts, see every post
 * with its numbers, pin up to three per platform and choose what the
 * website shows. The same screen serves the doctor and, inside a client
 * scope, BrandEx staff in the admin panel.
 */
export function SocialView() {
  const t = useT();
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  const social = useSocial(orgId);
  const { tab, go } = usePlatformTab();
  const back = useSignInReturn();
  if (orgs.isPending) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || orgId === undefined) return <Alert tone="error">{errorMessage(orgs.error, t)}</Alert>;
  const panels = social.data ?? [];
  const panel = tab === null ? undefined : panels.find((p) => p.platform === tab);
  return (
    <div className="grid gap-6">
      <header className="max-w-2xl">
        <h1 className="font-display text-2xl font-bold">{t("social.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t(orgs.staff ? "social.subtitle.staff" : "social.subtitle")}</p>
      </header>

      <nav aria-label={t("social.tabs")} className="-mx-1 flex gap-1 overflow-x-auto border-b border-border px-1">
        <TabButton active={tab === null} onClick={() => go(null)} icon={<LayoutGrid aria-hidden className="h-4 w-4" />} label={t("social.overview")} />
        {SOCIAL_KEYS.map((k) => {
          const p = panels.find((x) => x.platform === k);
          const warn = p?.sync?.status === "needs_reconnect" || p?.sync?.status === "failing";
          return (
            <TabButton
              key={k}
              active={tab === k}
              onClick={() => go(k)}
              icon={<PlatformIcon platform={k} className="h-4 w-4" />}
              label={t(`social.platform.${k}` as MessageKey)}
              dot={warn ? "warn" : p?.connected === true ? "ok" : undefined}
            />
          );
        })}
      </nav>

      {back.pick !== null ? <ChooseAccount orgId={orgId} id={back.pick} onDone={back.clear} /> : null}
      {back.error !== null ? (
        <Alert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            {t(`social.signIn.${back.error}` as MessageKey)}
            <button type="button" className="font-medium underline" onClick={back.clear}>
              {t("social.signIn.dismiss")}
            </button>
          </span>
        </Alert>
      ) : null}
      {social.isPending ? <Spinner label={t("common.loading")} /> : null}
      {social.isError ? <Alert tone="error">{errorMessage(social.error, t)}</Alert> : null}
      {social.isSuccess && tab === null ? <Overview panels={panels} onOpen={go} /> : null}
      {social.isSuccess && tab !== null && panel !== undefined ? <PlatformPanel key={tab} orgId={orgId} platform={tab} panel={panel} staff={orgs.staff} /> : null}
    </div>
  );
}

function TabButton({ active, onClick, icon, label, dot }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; dot?: "ok" | "warn" | undefined }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
      {dot !== undefined ? <span aria-hidden className={cn("h-2 w-2 rounded-full", dot === "ok" ? "bg-mint-500" : "bg-coral-500")} /> : null}
    </button>
  );
}

function Overview({ panels, onOpen }: { panels: SocialPlatform[]; onOpen: (k: SocialKey) => void }) {
  const t = useT();
  const { locale } = useBrandex();
  return (
    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {panels.filter((p): p is SocialPlatform & { platform: SocialKey } => isSocialKey(p.platform)).map((p) => {
        const k = p.platform;
        const status = p.sync?.status;
        const followers = k === "google" ? p.sync?.stats?.posts : p.sync?.stats?.followers;
        return (
          <li key={k}>
            <button
              type="button"
              onClick={() => onOpen(k)}
              className="grid h-full w-full content-start gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4 text-left transition hover:border-primary/60 hover:shadow-sm"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 font-semibold">
                  <PlatformIcon platform={k} className="h-6 w-6" />
                  {t(`social.platform.${k}` as MessageKey)}
                </span>
                {p.connected && status !== undefined ? (
                  <Badge tone={STATUS_TONE[status]} className="shrink-0 whitespace-nowrap">{t(`social.status.${status}` as MessageKey)}</Badge>
                ) : (
                  <Badge className="shrink-0 whitespace-nowrap">{t(p.configured ? "social.status.not_connected" : "social.status.soon")}</Badge>
                )}
              </span>
              {p.connected ? (
                <>
                  <span className="truncate text-sm text-muted-foreground">{p.sync?.profile.handle ?? p.sync?.profile.name}</span>
                  <span className="font-display text-2xl font-bold">
                    {followers === null || followers === undefined ? "—" : compact(locale, followers)}
                    <span className="ml-1.5 text-sm font-normal text-muted-foreground">{t(k === "youtube" ? "social.stats.subscribers" : k === "google" ? "social.stats.reviews" : "social.stats.followers")}</span>
                  </span>
                  <span className="text-sm text-muted-foreground">{t("social.overview.posts", { posts: p.posts, pinned: p.pinned, max: p.max_pinned })}</span>
                  <span className="text-xs text-muted-foreground">
                    {t(onSite(p) ? "social.site.on" : "social.site.off")}
                    {p.sync?.last_synced_at ? ` · ${t("social.overview.synced", { when: formatRelative(locale, p.sync.last_synced_at) })}` : ""}
                  </span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">{t(p.configured ? "social.overview.connect" : "social.overview.soon", { platform: t(`social.platform.${k}` as MessageKey) })}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
